<template>
  <div class="n8n-page">
    <!-- Header -->
    <header class="n8n-header">
      <div class="n8n-header__brand">
        <img src="../assets/images/logo2.png" class="n8n-header__logo" loading="lazy" alt="Logo" />
        <div class="n8n-header__info">
          <span class="n8n-header__title">UFA Easy888</span>
          <span class="n8n-header__badge">{{ code }}</span>
        </div>
      </div>
      <div class="n8n-header__avatar" :style="{ backgroundImage: `url(${pictureUrl})` }"></div>
    </header>

    <!-- Main Content -->
    <main class="n8n-main">
      <!-- Page Hero -->
      <div class="n8n-hero">
        <div class="n8n-hero__icon">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M12 2v20M2 12h20" stroke-linecap="round"/>
          </svg>
        </div>
        <h1 class="n8n-hero__title">แจ้งฝากเงิน</h1>
        <p class="n8n-hero__subtitle">ฝากผ่าน Easy แล้วรับเครดิตที่ Shiba88</p>
      </div>

      <div class="n8n-migration-notice" role="status">
        <strong>ย้ายระบบไป Shiba88 แล้ว</strong>
        <p>ยอดฝากผ่านหน้านี้จะเติมเข้าบัญชี Shiba88 โดยอัตโนมัติ ไม่เติมเครดิตหรือเหรียญใน Easy อีก</p>
        <p>แอดมินจะแจ้งยูส รหัสผ่าน และลิงก์เข้า Shiba88 ให้คุณ</p>
      </div>

      <!-- Step Cards -->
      <div class="n8n-steps">
        <!-- Step 1 -->
        <div class="n8n-card n8n-card--step">
          <div class="n8n-step-header">
            <span class="n8n-step-number">1</span>
            <span class="n8n-step-title">บัญชีของคุณ</span>
          </div>
          <div class="n8n-step-content">
            <p class="n8n-text">ใช้บัญชีของคุณโอนเข้ามา</p>
            <div class="n8n-info-box">
              <svg class="n8n-info-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <rect x="3" y="3" width="18" height="18" rx="2"/>
                <path d="M3 9h18M9 21V9"/>
              </svg>
              <span>{{ mybank }}</span>
            </div>
            <div class="n8n-warning-box">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
              </svg>
              <span>กรุณาโอนเงินมากกว่า 10 บาท</span>
            </div>
          </div>
        </div>

        <!-- Step 2 -->
        <div class="n8n-card n8n-card--step">
          <div class="n8n-step-header">
            <span class="n8n-step-number">2</span>
            <span class="n8n-step-title">โอนเข้าบัญชี</span>
          </div>
          <div class="n8n-step-content">
            <div v-if="customBanksLoading" class="n8n-bank-state">
              กำลังโหลดบัญชีฝาก...
            </div>
            <div v-if="customBanksError" class="n8n-bank-alert">
              {{ customBanksError }}
            </div>
            <div
              v-if="!customBanksLoading && !customBanksError && !depositBankCards.length"
              class="n8n-bank-state"
            >
              ขณะนี้ไม่มีบัญชีรับฝากที่เปิดใช้งาน กรุณาติดต่อแอดมินก่อนโอนเงิน
            </div>

            <div class="n8n-bank-list">
              <div
                v-for="bank in depositBankCards"
                :key="bank.id"
                class="n8n-bank-card"
              >
                <div class="n8n-bank-logo">
                  <img
                    :src="bank.image"
                    loading="lazy"
                    :alt="bank.bankName"
                  />
                </div>
                <div class="n8n-bank-details">
                  <div class="n8n-bank-name">{{ bank.accountName }}</div>
                  <div class="n8n-bank-code">{{ bank.bankName }}</div>
                  <div class="n8n-bank-number">{{ bank.accountNo }}</div>
                </div>
                <button
                  class="n8n-btn n8n-btn--copy"
                  v-clipboard:copy="bank.copyValue"
                  v-clipboard:success="onCopySuccess"
                  v-clipboard:error="onCopyError"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <rect x="9" y="9" width="13" height="13" rx="2"/>
                    <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/>
                  </svg>
                  คัดลอก
                </button>
              </div>
            </div>
            
            <!-- Copy Success Toast -->
            <transition name="n8n-toast">
              <div v-if="showCopySuccess" class="n8n-toast n8n-toast--success">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                </svg>
                <span>คัดลอกเลขบัญชีสำเร็จ!</span>
              </div>
            </transition>
          </div>
        </div>

        <!-- Step 3 -->
        <div class="n8n-card n8n-card--step">
          <div class="n8n-step-header">
            <span class="n8n-step-number">3</span>
            <span class="n8n-step-title">อัปโหลดสลิป</span>
          </div>
          <div class="n8n-step-content">
            <input
              ref="slipFileInput"
              class="n8n-slip-input"
              type="file"
              accept="image/*"
              @change="handleSlipFileChange"
            />

            <div class="n8n-slip-actions">
              <button
                class="n8n-btn n8n-btn--upload"
                type="button"
                @click="openSlipPicker"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M12 3v12"/>
                  <path d="M7 8l5-5 5 5"/>
                  <path d="M5 21h14"/>
                </svg>
                เลือกสลิป
              </button>
              <button
                class="n8n-btn n8n-btn--submit"
                type="button"
                :disabled="!canUploadSlip || slipUploading"
                @click="uploadSlip"
              >
                {{ slipUploading ? "กำลังส่ง..." : "ส่งสลิปตรวจสอบ" }}
              </button>
            </div>

            <div v-if="slipFileName" class="n8n-slip-file">
              <span>{{ slipFileName }}</span>
              <button type="button" @click="clearSlipFile">ลบ</button>
            </div>
            <img
              v-if="slipPreview"
              :src="slipPreview"
              class="n8n-slip-preview"
              alt="Slip preview"
            />
            <div v-if="slipUploadMessage" class="n8n-bank-state n8n-bank-state--success">
              {{ slipUploadMessage }}
            </div>
            <div v-if="slipUploadError" class="n8n-bank-alert">
              {{ slipUploadError }}
            </div>
          </div>
        </div>
      </div>

      <!-- Info Footer -->
      <div class="n8n-info-footer">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/>
        </svg>
        <span>ระบบจะเติมเครดิตเข้า Shiba88 อัตโนมัติภายใน 1-3 นาที</span>
      </div>
    </main>

    <!-- Bottom Menu -->
    <BottomMenu></BottomMenu>
  </div>
</template>

<script>
import firebase from "firebase/app";
import "firebase/firestore";
import "firebase/database";
import moment from "moment-timezone";
import router from "./../router";
import liffService from "@/services/liff";
import {
  formatAccountNo,
  getCustomBanksFirebaseApp,
  getCustomBanksSiteConfig,
  getVisibleCustomBanks
} from "@/services/customBanks";
const axios = require("axios").default;
export default {
  name: "Deposit",
  components: {
    BottomMenu: require("./BottomMenu").default
  },
  created() {
    // LIFF is already initialized by router guard
    this.loadUserData();
  },
  data() {
    return {
      user:[],
      menuVisible: false,
      pictureUrl: "",
      displayName: "",
      mybank: "",
      userId: null,
      userKey: "",
      code: "",
      showCopySuccess: false,
      customDepositBanks: [],
      customBanksLoading: false,
      customBanksError: "",
      slipUsageToday: 0,
      slipFileName: "",
      slipPreview: "",
      slipImageData: "",
      slipUploading: false,
      slipUploadMessage: "",
      slipUploadError: "",
      slipUploadEndpoint:
        "https://asia-east2-ufs896vip.cloudfunctions.net/createSlipUpload",
      shibaSiteConfig: getCustomBanksSiteConfig("shiba-88")
    };
  },
  computed: {
    depositBankCards() {
      return this.customDepositBanks;
    },
    canUploadSlip() {
      return !!this.slipImageData && !!this.userId;
    }
  },
  methods: {
    validateNumber(event) {
      return event.charCode >= 48 && event.charCode <= 57;
    },
    setAmount(amount) {
      this.money = amount;
      this.error = 0;
    },
    onCopySuccess() {
      this.showCopySuccess = true;
      setTimeout(() => {
        this.showCopySuccess = false;
      }, 2000);
    },
    onCopyError() {
      alert("ไม่สามารถคัดลอกได้ กรุณาลองใหม่");
    },
    copy() {
      const testingCodeToCopy = document.querySelector("#copy-bank");
      if (!testingCodeToCopy) return;
      testingCodeToCopy.setAttribute("type", "text");
      testingCodeToCopy.select();
      try {
        const successful = document.execCommand("copy");
        const msg = successful ? "เรียบร้อย" : "ไม่สำเร็จ";
        alert("คัดลอกเลขบัญชี " + msg);
      } catch (err) {
        alert("Oops, unable to copy");
      }

      /* unselect the range */
      testingCodeToCopy.setAttribute("type", "hidden");
      window.getSelection().removeAllRanges();
    },
    async loadDepositPageData() {
      await this.loadCustomDepositBanks();
      this.loadSlipUsageStatus();
    },
    async loadCustomDepositBanks() {
      this.customBanksLoading = true;
      this.customBanksError = "";

      try {
        const snapshot = await firebase
          .database()
          .ref("/bank/deposit/customBanks")
          .once("value");
        this.customDepositBanks = this.buildCustomDepositBanks(
          snapshot.val()
        );
      } catch (error) {
        this.customDepositBanks = [];
        this.customBanksError = "โหลดบัญชีจากหลังบ้านไม่ได้";
      } finally {
        this.customBanksLoading = false;
      }
    },
    async loadSlipUsageStatus() {
      try {
        const shibaApp = getCustomBanksFirebaseApp(this.shibaSiteConfig);
        const shibaDatabase = shibaApp.database();
        const today = moment()
          .tz("Asia/Bangkok")
          .format("YYYY-MM-DD");
        const snapshots = await Promise.all([
          shibaDatabase.ref(`/slip2go/usage/${today}`).once("value"),
          shibaDatabase.ref(`/easyslip/usage/${today}`).once("value")
        ]);
        this.slipUsageToday =
          Number(snapshots[0].val() || 0) + Number(snapshots[1].val() || 0);
      } catch (error) {
        this.slipUsageToday = 0;
      }
    },
    buildCustomDepositBanks(customBanks) {
      return getVisibleCustomBanks(customBanks).map((bank, index) => {
        const bankName = this.getBankDisplayName(bank.bankCode);
        const accountName =
          `${bank.firstname || ""} ${bank.lastname || ""}`.trim() || bankName;
        const accountNo = bank.accountno || "";

        return {
          id: `custom-bank-${index}-${formatAccountNo(accountNo)}`,
          bankName,
          accountName,
          accountNo,
          image: this.getBankImage(bank.bankCode),
          copyValue: formatAccountNo(accountNo)
        };
      });
    },
    normalizeBankCode(bankCode) {
      return (bankCode || "").toString().trim().toLowerCase();
    },
    getBankDisplayName(bankCode) {
      const normalized = this.normalizeBankCode(bankCode);
      const map = {
        scb: "ธนาคารไทยพาณิชย์",
        kbank: "ธนาคารกสิกรไทย",
        krungsri: "ธนาคารกรุงศรีอยุธยา",
        bay: "ธนาคารกรุงศรีอยุธยา",
        ktb: "ธนาคารกรุงไทย",
        bbl: "ธนาคารกรุงเทพ",
        bankokbank: "ธนาคารกรุงเทพ",
        tmb: "ธนาคารทหารไทยธนชาต",
        ttb: "ธนาคารทหารไทยธนชาต",
        gsb: "ธนาคารออมสิน",
        lh: "ธนาคารแลนด์ แอนด์ เฮ้าส์",
        baac: "ธ.ก.ส.",
        cimb: "ธนาคาร CIMB",
        uob: "ธนาคาร UOB",
        kiatnakin: "ธนาคารเกียรตินาคินภัทร",
        ghbank: "ธนาคารอาคารสงเคราะห์",
        tisco: "ธนาคารทิสโก้",
        "ธนาคารอิสลาม": "ธนาคารอิสลาม",
        ibank: "ธนาคารอิสลาม",
        icbc: "ธนาคารไอซีบีซีไทย",
        standard: "ธนาคารสแตนดาร์ดชาร์เตอร์ดไทย",
        truewallet: "TrueMoney Wallet"
      };
      return map[normalized] || bankCode || "";
    },
    getBankImage(bankCode) {
      const normalized = this.normalizeBankCode(bankCode);
      const map = {
        scb: "scb.png",
        kbank: "kbank.png",
        krungsri: "krungsri.png",
        bay: "krungsri.png",
        ktb: "ktb.png",
        bbl: "bankokbank.png",
        bankokbank: "bankokbank.png",
        tmb: "tmb.png",
        ttb: "tmb.png",
        gsb: "gsb.png",
        lh: "lh.png"
      };

      if (normalized === "truewallet") {
        return require("../assets/images/truemoney.png");
      }

      if (!map[normalized]) return require("../assets/images/banking.png");

      try {
        return require("../assets/images/bank/" + map[normalized]);
      } catch (error) {
        return require("../assets/images/banking.png");
      }
    },
    openSlipPicker() {
      if (this.$refs.slipFileInput) {
        this.$refs.slipFileInput.click();
      }
    },
    async handleSlipFileChange(event) {
      const file = event.target.files && event.target.files[0];
      this.slipUploadMessage = "";
      this.slipUploadError = "";
      if (!file) return;
      if (!file.type || file.type.indexOf("image/") !== 0) {
        this.slipUploadError = "กรุณาเลือกไฟล์รูปภาพสลิป";
        this.clearSlipFile();
        return;
      }

      try {
        const imageData = await this.compressSlipImage(file);
        this.slipFileName = file.name;
        this.slipImageData = imageData;
        this.slipPreview = imageData;
      } catch (error) {
        this.clearSlipFile();
        this.slipUploadError = "อ่านไฟล์สลิปไม่ได้ กรุณาถ่ายรูปใหม่อีกครั้ง";
      }
    },
    clearSlipFile() {
      this.slipFileName = "";
      this.slipPreview = "";
      this.slipImageData = "";
      if (this.$refs.slipFileInput) {
        this.$refs.slipFileInput.value = "";
      }
    },
    compressSlipImage(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onerror = reject;
        reader.onload = () => {
          const image = new Image();
          image.onerror = reject;
          image.onload = () => {
            const maxSide = 1400;
            const ratio = Math.min(
              1,
              maxSide / Math.max(image.width, image.height)
            );
            const canvas = document.createElement("canvas");
            canvas.width = Math.max(1, Math.round(image.width * ratio));
            canvas.height = Math.max(1, Math.round(image.height * ratio));
            const context = canvas.getContext("2d");
            context.drawImage(image, 0, 0, canvas.width, canvas.height);

            let quality = 0.82;
            let dataUrl = canvas.toDataURL("image/jpeg", quality);
            while (dataUrl.length > 850000 && quality > 0.45) {
              quality -= 0.08;
              dataUrl = canvas.toDataURL("image/jpeg", quality);
            }
            resolve(dataUrl);
          };
          image.src = reader.result;
        };
        reader.readAsDataURL(file);
      });
    },
    async uploadSlip() {
      if (!this.canUploadSlip) {
        this.slipUploadError = "กรุณาเลือกไฟล์สลิปก่อนส่ง";
        return;
      }

      this.slipUploading = true;
      this.slipUploadMessage = "";
      this.slipUploadError = "";
      try {
        const now = moment()
          .tz("Asia/Bangkok")
          .format("YYYY-MM-DD HH:mm:ss");
        const response = await axios.post(this.slipUploadEndpoint, {
            status: "กำลังดำเนินการ",
            source: "line-liff",
            imageData: this.slipImageData,
            imageName: this.slipFileName,
            createdate: now,
            updatetime: now,
            userkey: this.userKey,
            userId: this.userId,
            code: this.code,
            firstname: this.user.firstname || "",
            lastname: this.user.lastname || "",
            bank: this.user.bank || "",
            "account_no": this.user["account_no"] || "",
            pictureUrl: this.pictureUrl,
            user: this.user || {}
          });
        if (!response.data || !response.data.status) {
          throw new Error(response.data && response.data.error ? response.data.error : "UPLOAD_FAILED");
        }
        this.clearSlipFile();
        this.slipUploadMessage = "ส่งสลิปแล้ว ระบบกำลังตรวจสอบ";
      } catch (error) {
        this.slipUploadError = "ส่งสลิปไม่สำเร็จ กรุณาลองใหม่";
      } finally {
        this.slipUploading = false;
      }
    },
    async confirm() {
      if (this.money >= 10 && this.userId) {
        this.isDisabled = true;
        this.error = 0;
        const rand = Math.floor(Math.random() * 50) + 1;
        const image = `https://firebasestorage.googleapis.com/v0/b/ufs896vip/o/coupon%2Fsummer%2F${rand}.jpg?alt=media`;
        await firebase
          .firestore()
          .collection("history")
          .add({
            userId: this.userId,
            pictureUrl: this.pictureUrl,
            linename: this.displayName,
            createdate: moment()
              .tz("Asia/Bangkok")
              .format("YYYY-MM-DD HH:mm:ss"),
            type: "ฝาก",
            qty: this.money,
            status: "กำลังดำเนินการ",
            updatetime: moment()
              .tz("Asia/Bangkok")
              .format("YYYY-MM-DD HH:mm:ss")
        });
        const profile = await window.liff.getProfile();
        await window.liff.sendMessages([
          {
          "type": "flex",
          "altText": "แจ้งเติมเครดิต",
          "contents": {
              "type": "bubble",
              "size": "kilo",
              "hero": {
                "type": "image",
                "url": image,
                "size": "full",
                "aspectRatio": "20:12",
                "aspectMode": "cover"
              },
              "body": {
                "type": "box",
                "layout": "vertical",
                "spacing": "md",
                "contents": [
                  {
                    "type": "text",
                    "text": "❤️ แจ้งเติมเครดิต ❤️ ",
                    "size": "lg",
                    "weight": "bold",
                    "align": "center"
                  },
                  {
                    "type": "box",
                    "layout": "vertical",
                    "spacing": "sm",
                    "contents": [
                      {
                        "type": "box",
                        "layout": "baseline",
                        "contents": [
                          {
                            "type": "text",
                            "text": "💵  จำนวน "+this.numberWithCommas(Number(this.money.toString().replace(/,/g, "")))+" บาท",
                            "margin": "sm",
                            "flex": 0,
                            "size": "md"
                          }
                        ]
                      },
                      {
                        "type": "box",
                        "layout": "baseline",
                        "contents": [
                          {
                            "type": "text",
                            "text": "📆 "+moment().tz("Asia/Bangkok").format("D MMM,YYYY HH:mm"),
                            "margin": "sm",
                            "flex": 0,
                            "size": "md"
                          }
                        ]
                      }
                    ]
                  },
                  {
                    "type": "text",
                    "text": "โปรดรอสักครู่",
                    "wrap": true,
                    "color": "#aaaaaa",
                    "size": "xs",
                    "align": "center"
                  }
                ]
              }
            }
          },
          {
            "type": "text",
            "text": "ถ้าหากลูกค้าโอนแล้วและรอเกิน 3 นาทีรบกวนส่งสลิปให้น้องออโต้ด้วยนะคะ 😘😘",
          }
        ]);
        window.liff.closeWindow();
      } else {
        this.$refs["money"].focus();
        this.error = 1;
      }
    },
    numberWithCommas(x) {
      return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    },
    async loadUserData() {
      const isLocalhost = window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";
      
      // Get profile from centralized service (already fetched by router guard)
      let profile = liffService.profile;
      
      // Mock LIFF profile for localhost development
      if (isLocalhost && !profile) {
        profile = {
          userId: "UmockUserIdForDev",
          displayName: "Dev Tester",
          pictureUrl: "",
          statusMessage: ""
        };
      }
      
      if (!profile) return;

      this.pictureUrl = profile.pictureUrl || "none";
      this.userId = profile.userId || "";
      this.displayName = profile.displayName;
      
      if (this.userId) {
        // Development mode on localhost
        if (isLocalhost) {
          this.code = "DEV001";
          this.mybank = "ธนาคารไทยพาณิชย์ - XXX-X-XXXXX-X";
          await this.loadDepositPageData();
          return;
        }
        
        const users = (
          await firebase
            .database()
            .ref(`/users`)
            .orderByChild("userId")
            .equalTo(this.userId)
            .once("value")
        ).val();
        if (!users) {
          router.replace("/register");
        } else {
          let user = {};
          let key = '';
          for (const x in users) {
            user = users[x];
            key = x;
          }
          this.user = user
          this.userKey = key;
          await this.storeIP(key);
          this.code = user.code;
          if (user.bank && user.account_no) {
            let bank = "";
            if (user.bank === "scb") bank = "ธนาคารไทยพาณิชย์";
            else if (user.bank === "bankokbank") bank = "ธนาคารกรุงเทพ";
            else if (user.bank === "kbank") bank = "ธนาคารกสิกรไทย";
            else if (user.bank === "ktb") bank = "ธนาคารกรุงไทย";
            else if (user.bank === "krungsri") bank = "ธนาคารกรุงศรีอยุธยา";
            else if (user.bank === "tmb") bank = "ธนาคารทหารไทย";
            else if (user.bank === "gsb") bank = "ธนาคารออมสิน";
            else if (user.bank === "lh") bank = "ธนาคารแลนด์ แอนด์ เฮ้าส์";
            else if (user.bank === "tisco") bank = "ธนาคารทิสโก้";
            else if (user.bank === "thanachart") bank = "ธนาคารธนชาต";
            else if (user.bank === "kiatnakin") bank = "ธนาคารเกียรตินาคิน";
            else if (user.bank === "uob") bank = "ธนาคารยูโอบี";
            else if (user.bank === "ธนาคารอิสลาม") bank = "ธนาคารอิสลาม";
            else if (user.bank === "ghbank") bank = "ธนาคาร อาคารสงเคราะห์";
            else if (user.bank === "ธนาคารนครหลวง") bank = "ธนาคาร นครหลวง";
            else if (user.bank === "cimb") bank = "ธนาคาร CIMB";
            else if (user.bank === "baac") bank = "ธนาคารเพื่อการเกษตรและสหกรณ์การเกษตร";
            else if (user.bank === "truewallet") bank = "TrueMoney Wallet";
            this.mybank = `${bank}-${user.account_no}`;
          }
          await this.loadDepositPageData();
        }
      }
    },
    async storeIP(key){
        await axios.get('https://api.ipify.org?format=json').then(x => {
          if(x.data.ip && key){
            firebase.database().ref(`/users/${key}`).update({
              ip:x.data.ip
            })
          }
        });
    }
  }
};
</script>

<style lang="scss" scoped>
// ============================================================================
// N8N THEME - DEPOSIT PAGE
// ============================================================================

// Design Tokens
$n8n-bg: #0a0a0a;
$n8n-surface: #151515;
$n8n-surface-hover: #1f1f1f;
$n8n-border: rgba(255, 255, 255, 0.08);
$n8n-primary: #FF6B35;
$n8n-primary-soft: rgba(255, 107, 53, 0.15);
$n8n-success: #10b981;
$n8n-success-soft: rgba(16, 185, 129, 0.15);
$n8n-warning: #f59e0b;
$n8n-warning-soft: rgba(245, 158, 11, 0.15);
$n8n-text: #ffffff;
$n8n-text-secondary: rgba(255, 255, 255, 0.6);
$n8n-text-muted: rgba(255, 255, 255, 0.4);
$n8n-radius: 12px;
$n8n-radius-sm: 8px;

.n8n-page {
  min-height: 100vh;
  background: transparent; // let #app cozy backdrop show through
  color: $n8n-text;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  padding-bottom: 100px;
}

// Header
.n8n-header {
  position: sticky;
  top: 0;
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.25rem;
  padding-top: calc(1rem + env(safe-area-inset-top, 0px));
  background: rgba(10, 10, 10, 0.85);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border-bottom: 1px solid $n8n-border;

  &__brand {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }

  &__logo {
    height: 44px;
    width: auto;
  }

  &__info {
    display: flex;
    flex-direction: column;
  }

  &__title {
    font-size: 1rem;
    font-weight: 600;
    color: $n8n-text;
  }

  &__badge {
    font-size: 0.75rem;
    color: $n8n-primary;
    font-weight: 500;
  }

  &__avatar {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background-size: cover;
    background-position: center;
    background-color: $n8n-surface;
    border: 2px solid $n8n-border;
  }
}

// Main Content
.n8n-main {
  padding: 1.5rem 1rem;
  max-width: 480px;
  margin: 0 auto;
}

// Hero Section
.n8n-hero {
  text-align: center;
  margin-bottom: 2rem;
  
  &__icon {
    width: 64px;
    height: 64px;
    margin: 0 auto 1rem;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, $n8n-success 0%, #059669 100%);
    border-radius: 16px;
    color: white;
    box-shadow: 0 8px 32px rgba(16, 185, 129, 0.3);
  }

  &__title {
    font-size: 1.75rem;
    font-weight: 700;
    margin: 0 0 0.5rem;
    background: linear-gradient(135deg, #fff 0%, rgba(255,255,255,0.8) 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  &__subtitle {
    font-size: 0.95rem;
    color: $n8n-text-secondary;
    margin: 0;
  }
}

.n8n-migration-notice {
  margin: -0.5rem 0 1.5rem;
  padding: 1rem;
  border: 1px solid rgba(16, 185, 129, 0.45);
  border-radius: $n8n-radius;
  background: $n8n-success-soft;

  strong {
    display: block;
    margin-bottom: 0.5rem;
    color: $n8n-success;
  }

  p {
    margin: 0.35rem 0 0;
    color: $n8n-text;
    font-size: 0.875rem;
    line-height: 1.5;
  }
}

// Cards
.n8n-card {
  background: $n8n-surface;
  border: 1px solid $n8n-border;
  border-radius: $n8n-radius;
  margin-bottom: 1rem;
  overflow: hidden;
  
  &--step {
    .n8n-step-header {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      padding: 1rem 1.25rem;
      background: rgba(255, 255, 255, 0.02);
      border-bottom: 1px solid $n8n-border;
    }
    
    .n8n-step-number {
      width: 28px;
      height: 28px;
      display: flex;
      align-items: center;
      justify-content: center;
      background: $n8n-primary;
      color: white;
      font-size: 0.875rem;
      font-weight: 700;
      border-radius: 50%;
    }
    
    .n8n-step-title {
      font-size: 1rem;
      font-weight: 600;
      color: $n8n-text;
    }
    
    .n8n-step-content {
      padding: 1.25rem;
    }
  }
}

// Text
.n8n-text {
  font-size: 0.9rem;
  color: $n8n-text-secondary;
  margin: 0 0 1rem;
  line-height: 1.5;
}

// Info Box
.n8n-info-box {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.875rem 1rem;
  background: $n8n-primary-soft;
  border: 1px solid rgba(255, 107, 53, 0.2);
  border-radius: $n8n-radius-sm;
  margin-bottom: 0.75rem;
  
  .n8n-info-icon {
    color: $n8n-primary;
    flex-shrink: 0;
  }
  
  span {
    font-size: 0.875rem;
    color: $n8n-primary;
    font-weight: 500;
  }
}

// Warning Box
.n8n-warning-box {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background: $n8n-warning-soft;
  border-radius: $n8n-radius-sm;
  
  svg {
    color: $n8n-warning;
    flex-shrink: 0;
  }
  
  span {
    font-size: 0.8rem;
    color: $n8n-warning;
    font-weight: 500;
  }
}

.n8n-provider-strip {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.n8n-provider-chip {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  padding: 0.65rem 0.7rem;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid $n8n-border;
  border-radius: $n8n-radius-sm;

  strong {
    overflow: hidden;
    color: $n8n-text;
    font-size: 0.76rem;
    font-weight: 700;
    line-height: 1.15;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  span {
    color: $n8n-text-muted;
    font-size: 0.68rem;
    font-weight: 700;
    line-height: 1;
  }

  &--ready {
    background: $n8n-success-soft;
    border-color: rgba(16, 185, 129, 0.28);

    span {
      color: $n8n-success;
    }
  }
}

.n8n-bank-state,
.n8n-bank-alert {
  margin-bottom: 1rem;
  padding: 0.75rem 0.875rem;
  border-radius: $n8n-radius-sm;
  font-size: 0.8rem;
  font-weight: 500;
  line-height: 1.4;
}

.n8n-bank-state {
  background: rgba(255, 255, 255, 0.04);
  color: $n8n-text-secondary;
}

.n8n-bank-alert {
  background: $n8n-warning-soft;
  color: $n8n-warning;
}

.n8n-bank-state--success {
  margin-top: 1rem;
  background: $n8n-success-soft;
  color: $n8n-success;
}

.n8n-slip-input {
  display: none;
}

.n8n-slip-actions {
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.75rem;
}

.n8n-slip-file {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-top: 0.85rem;
  padding: 0.75rem 0.875rem;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid $n8n-border;
  border-radius: $n8n-radius-sm;

  span {
    min-width: 0;
    overflow: hidden;
    color: $n8n-text-secondary;
    font-size: 0.8rem;
    font-weight: 600;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  button {
    flex: 0 0 auto;
    padding: 0;
    background: transparent;
    border: 0;
    color: $n8n-warning;
    font-size: 0.8rem;
    font-weight: 700;
  }
}

.n8n-slip-preview {
  display: block;
  width: 100%;
  max-height: 360px;
  margin-top: 0.85rem;
  object-fit: contain;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid $n8n-border;
  border-radius: $n8n-radius-sm;
}

.n8n-bank-list {
  display: grid;
  gap: 0.875rem;
}

// Bank Card
.n8n-bank-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 1.5rem;
  background: linear-gradient(135deg, rgba(255,255,255,0.03) 0%, rgba(255,255,255,0.01) 100%);
  border: 1px solid $n8n-border;
  border-radius: $n8n-radius;
}

.n8n-bank-logo {
  width: 180px;
  height: 60px;
  background: white;
  border-radius: $n8n-radius-sm;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem;
  margin-bottom: 1.25rem;
  
  img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
  }
}

.n8n-bank-details {
  margin-bottom: 1.25rem;
}

.n8n-bank-name {
  font-size: 1rem;
  font-weight: 600;
  color: $n8n-text;
  margin-bottom: 0.25rem;
}

.n8n-bank-code {
  color: $n8n-text-secondary;
  font-size: 0.78rem;
  font-weight: 500;
  line-height: 1.3;
  margin-bottom: 0.4rem;
}

.n8n-bank-number {
  font-size: 1.5rem;
  font-weight: 700;
  color: $n8n-primary;
  letter-spacing: 1px;
  font-family: 'SF Mono', 'Consolas', monospace;
}

// Buttons
.n8n-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 50px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &--copy {
    background: linear-gradient(135deg, $n8n-primary 0%, #E55520 100%);
    color: white;
    box-shadow: 0 4px 16px rgba(255, 107, 53, 0.3);
    
    &:active {
      transform: scale(0.96);
      box-shadow: 0 2px 8px rgba(255, 107, 53, 0.3);
    }
  }

  &--upload {
    width: 100%;
    background: rgba(255, 255, 255, 0.08);
    color: $n8n-text;
    border: 1px solid $n8n-border;
  }

  &--submit {
    width: 100%;
    background: linear-gradient(135deg, $n8n-success 0%, #059669 100%);
    color: white;

    &:disabled {
      cursor: not-allowed;
      opacity: 0.45;
    }
  }
}

// Toast
.n8n-toast {
  position: fixed;
  bottom: 100px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.875rem 1.25rem;
  border-radius: 50px;
  font-size: 0.875rem;
  font-weight: 500;
  z-index: 1000;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  
  &--success {
    background: $n8n-success;
    color: white;
  }
}

.n8n-toast-enter-active,
.n8n-toast-leave-active {
  transition: all 0.3s ease;
}

.n8n-toast-enter,
.n8n-toast-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(20px);
}

// Info Footer
.n8n-info-footer {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 1rem;
  margin-top: 1rem;
  color: $n8n-text-muted;
  font-size: 0.85rem;
  
  svg {
    color: $n8n-text-muted;
  }
}
</style>
